import React, {useContext, useEffect, useState} from "react";
import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import {GridReactContext} from "../../../../Utils/DataGridWrapper/GridContext";
import {DataGridWrapper} from "../../../../Utils/DataGridWrapper/DataGridWrapper";
import {DeleteUsersTableButton} from "../../../BulkDeleteUsers/DeleteUsersTableButton";
import {EditUserCell} from "../../../Users/EditUserCell/EditUserCell";
import Tooltip from '@mui/material/Tooltip';
import {get} from "lodash";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {getOrganizationUsersByCount} from "../../../../Services/api";
import {PhoneCell} from "../../../../Utils/PhoneCell/PhoneCell";

/**
 * Get column definitions for the organization users table in the removal modal.
 * Uses DataGrid format (field, headerName, renderCell) for compatibility with DataGridWrapper.
 *
 * @param {object} params - The function parameters
 * @param {object} params.i18n - The i18n translation context
 * @returns {Array<object>} Array of column definitions for DataGridWrapper
 */
const getOrganizationalUnitColumns = ({i18n}) => {
    return [
        {
            field: 'firstName',
            headerName: i18n.translate("allUsersTable.columns.fullName"),
            minWidth: 200,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'email',
            headerName: i18n.translate("allUsersTable.columns.email"),
            minWidth: 250,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'phoneFormatted',
            headerName: i18n.translate("allUsersTable.columns.phone"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <PhoneCell value={params.value} />,
        },
        {
            field: 'id',
            headerName: i18n.translate("allUsersTable.columns.edit"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <EditUserCell user={params.row}/>,
        }
       
    ];
}

/**
 * Filters and converts user data to show only users who belong exclusively to this organization.
 * These are the users who would be affected by organization deletion.
 *
 * @param {Array<object>} users - Array of user objects from API
 * @returns {Array<object>} Filtered array of users belonging only to this organization
 */
const convertData = (users) => {
    return users?.filter((user) => {
        return user.organizations && user.organizations.length === 1 && !user.isCompanyAdmin
    })
}

const paginationModel = {
    page: 0,
    pageSize: 20,
}

/**
 * Modal content component that manages the organization users table with selection and bulk deletion.
 * Displays users belonging to the organization and allows bulk deletion before removing the organization.
 *
 * @param {object} params - Component parameters
 * @param {string} params.organizationId - The ID of the organization
 * @param {string} params.organizationName - The name of the organization
 * @param {object} params.localTableFilters - Filter configuration for the table
 * @param {function} params.onUserDeleted - Callback when users are deleted
 * @param {function} params.onRemoveOrg - Callback to remove the organization
 * @param {function} params.onModalClose - Callback to close the modal
 * @param {number} params.organizationUsersCount - Current count of users in the organization
 * @returns {JSX.Element} The modal content with user table and bulk delete functionality
 */
export function RemoveOrgModalContent({organizationId, organizationName, localTableFilters, onUserDeleted, onRemoveOrg, onModalClose, organizationUsersCount}) {
    const {i18n} = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);
    const [selectionModel, setSelectionModel] = useState([]);

    // Sync local selection state with GridContext for bulk delete button
    useEffect(() => {
        gridContext.setSelectionModelToContext(selectionModel);
    }, [selectionModel, gridContext]);

    const getColumns = async () => {
        return getOrganizationalUnitColumns({i18n})
    }

    const apiToGetData = async ({paginationModel, filterModel}) => {
        const organizationUsers = await getOrganizationUsersByCount({paginationModel, filterModel})
        return {data: get(organizationUsers, "data"), count: get(organizationUsers, "count")}
    }

    const onSelectionModelChange = ({newSelectionModel}) => {
        setSelectionModel(newSelectionModel);
    };

    // After users are deleted, refresh the table
    const handleUserDeleted = () => {
        setSelectionModel([]); // Clear selection
        gridContext.triggerTableRefresh();
        if (onUserDeleted) {
            onUserDeleted();
        }
    };

    const tableButtons = [
        {
            type: 'ComponentButton',
            component: () => (
                <DeleteUsersTableButton 
                    onSuccess={handleUserDeleted}
                />
            )
        }
    ];

    return (
        <div className="removeOrgModal modalWrapper">
            <div className="modalTitle">
                {i18n.translate('superAdmin.allOrgsTable.deleteOrgTitle')} {organizationName}
            </div>
            <div className="modalContent">
                <div className="modalContentTitle">
                    {organizationUsersCount > 0 
                        ? i18n.translate('organizationalUnitTable.removeOrgCell.modalNotAllowedDeleteTitle')
                        : i18n.translate('organizationalUnitTable.removeOrgCell.modalAllowDeleteTitle')}
                </div>
                {organizationUsersCount > 0 && (
                    <DataGridWrapper
                        buttons={tableButtons}
                        filterModel={localTableFilters}
                        disableColumnFilter
                        paginationModel={paginationModel}
                        getColumns={getColumns}
                        apiToGetData={apiToGetData}
                        dataConverter={convertData}
                        checkboxSelection
                        selectionModel={selectionModel}
                        onSelectionModelChange={onSelectionModelChange}
                        keepNonExistentRowsSelected
                        isRowSelectable={(params) => {
                            const roles = get(params, 'row.roles') || {};
                            const isCompanyAdmin = get(params, 'row.isCompanyAdmin') || false;
                            return !(roles.SuperAdmin || isCompanyAdmin);
                        }}
                    />
                )}
            </div>
            <div className="modalFooter">
                <Button 
                    color={buttonColors.YELLOW}
                    size="small"
                    text={i18n.translate('general.cancel')}
                    onClick={onModalClose}
                />
                <Tooltip title={organizationUsersCount > 0 ? i18n.translate('organizationalUnitTable.removeOrgCell.tooltipMessage') : ''}>
                    <span>
                        <Button 
                            color={buttonColors.GREY}
                            disabled={organizationUsersCount !== 0}
                            size="small"
                            text={i18n.translate('superAdmin.allOrgsTable.deleteOrgConfirm')}
                            onClick={onRemoveOrg}
                        />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
}

